import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";

import { updatedDiff } from "deep-object-diff";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, DatePicker, Link, Modal, NumberInput, Text, YesNoRadioButton } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { CreditService } from "services";
import { CreditResponse } from "types";

const { REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID, REQUEST_PENDING } = ServiceConstants;
interface CreditDetailsProps {
  credit: CreditResponse;
  readOnly?: boolean;
}

const CreditDetails = ({ credit, readOnly = false }: CreditDetailsProps) => {
  const { t } = useTranslation();

  const timeoutRefs = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    setValues();
  }, [credit]);

  const updateCredit = useCallback(async (item: any) => {
    for (const propertyName in item) {
      if (timeoutRefs.current.has(propertyName)) {
        clearTimeout(timeoutRefs.current.get(propertyName));
      }
      timeoutRefs.current.set(
        propertyName,
        setTimeout(() => update(propertyName, item[propertyName]), 1000),
      );
    }
  }, []);

  const update = useCallback(async (propertyName: string, propertyValue: any) => {
    await CreditService.creditsUpdateProperty(credit.id, propertyName, propertyValue);
    timeoutRefs.current.delete(propertyName);
  }, []);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {},
    onValuesChange(values, previous) {
      updateCredit(updatedDiff(previous, values));
    },
  });

  const setValues = useCallback(() => {
    form.setValues({
      grantedCreditAmount: credit.grantedCreditAmount,
      grantedRepaymentMonths: credit.grantedRepaymentMonths,
      grantedInstallmentAmount: credit.grantedInstallmentAmount,
      firstInstallmentDate: credit.firstInstallmentDate
        ? new Date(credit.firstInstallmentDate)
        : undefined,
      securedLoanInstallment: credit.securedLoanInstallment,
      postponedInstallments: credit.postponedInstallments,
    });
  }, []);

  return (
    <Box>
      <SimpleGrid cols={4}>
        <NumberInput
          label={t("credit.grantedCreditAmount.label")}
          key={form.key("grantedCreditAmount")}
          {...form.getInputProps("grantedCreditAmount")}
          disabled={readOnly}
        />
        <NumberInput
          label={t("credit.grantedRepaymentMonths.label")}
          key={form.key("grantedRepaymentMonths")}
          {...form.getInputProps("grantedRepaymentMonths")}
          plain={true}
          disabled={readOnly}
        />
        <NumberInput
          label={t("credit.grantedInstallmentAmount.label")}
          key={form.key("grantedInstallmentAmount")}
          {...form.getInputProps("grantedInstallmentAmount")}
          allowDecimal={true}
          decimalScale={2}
          fixedDecimalScale={true}
          disabled={readOnly}
        />
        <DatePicker
          label={t("credit.firstInstallmentDate.label")}
          key={form.key("firstInstallmentDate")}
          {...form.getInputProps("firstInstallmentDate")}
          disabled={readOnly}
        />
      </SimpleGrid>
      <SimpleGrid cols={4} mt={"md"}>
        <YesNoRadioButton
          label={t("credit.securedLoanInstallment.label")}
          name={"securedLoanInstallment"}
          form={form}
          t={t}
          disabled={readOnly}
        />
      </SimpleGrid>
      {!readOnly && (
        <SimpleGrid cols={4} mt={"sm"}>
          <PostponeInstallment credit={credit} form={form} />
        </SimpleGrid>
      )}
    </Box>
  );
};

interface IPostponeInstallment {
  credit: CreditResponse;
  form: UseFormReturnType<any, any>;
}

const PostponeInstallment = ({ credit, form }: IPostponeInstallment) => {
  const { t } = useTranslation();

  const POSTPONING_MAX_NUMBER = 2;
  const postponeInstallmentServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID],
  );
  const postponedInstallments = form.getTransformedValues().postponedInstallments ?? 0;
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const postponeInstallment = useCallback(async () => {
    if (postponedInstallments >= 2) {
      return;
    }
    await CreditService.creditPostponeInstalment(credit.id);
    form.setFieldValue("postponedInstallments", postponedInstallments + 1);
    setModalVisible(false);
  }, [postponedInstallments]);

  const isLoading = useMemo(
    () => postponeInstallmentServiceObject?.status === REQUEST_PENDING,
    [postponeInstallmentServiceObject],
  );

  return (
    <>
      <Box>
        <Button
          variant={"outline"}
          onClick={() => setModalVisible(true)}
          loading={isLoading}
          disabled={isLoading || postponedInstallments >= 2}
          mt={20}
          fullWidth={true}
        >
          {t("credit.postponeInstallment.button")}
        </Button>
        <Text size={"sm"} c={"var(--mantine-color-gray-7)"} ta={"center"}>
          {postponedInstallments >= POSTPONING_MAX_NUMBER
            ? t("credit.postponeInstallment.limit", { max: POSTPONING_MAX_NUMBER })
            : t("credit.postponeInstallment.info", {
                numberOf: postponedInstallments,
                max: POSTPONING_MAX_NUMBER,
              })}
        </Text>
      </Box>
      <Modal opened={modalVisible} onClose={() => setModalVisible(false)} withCloseButton={false}>
        <Text ta={"center"} mt={24} mb={12} style={{ fontWeight: "bold" }}>
          {t("credit.postponeInstallment.modal.title")}
        </Text>
        <Text ta={"center"} mb={24}>
          {t("credit.postponeInstallment.modal.description")}
        </Text>
        <Flex mt={32} mb={12} justify={"space-between"} align={"center"} px={32}>
          <Link
            onClick={() => setModalVisible(false)}
            disabled={isLoading}
            c={"var(--mantine-color-gray-7)"}
          >
            {t("credit.postponeInstallment.modal.cancel")}
          </Link>
          <Button onClick={postponeInstallment} disabled={isLoading}>
            <Text>{t("credit.postponeInstallment.modal.submit")}</Text>
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default CreditDetails;
